import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Pencil, Trash2, Plus, X } from 'lucide-react';
import { dashboardApi } from '@/services/dashboardApi';
import { useLoad, Loading, Table, Badge, Search, Kpi, Block, eur, money, fdate, todayLocal, inputCls, btnGold, btnGhost } from './ui';
import { useConfirm } from '../ConfirmDialog';

/*
  Gastos del CDM (antes hoja GASTOS). Alta, edición y borrado; todo queda en la auditoría del CDM.
  Los gastos de la partida «Comisiones» se cruzan con las comisiones marcadas como pagadas.
*/
// Función (no constante) para que la fecha sea siempre la del día en que se abre el formulario
const emptyExpense = () => ({ date: todayLocal(), category: '', concept: '', supplier: '', amount: '', currency: 'EUR', recurring: false, notes: '' });

function ExpenseForm({ initial, categories, onCancel, onSave, busy }) {
  const [f, setF] = useState(initial);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  const ok = f.date && f.category && Number(f.amount) > 0;
  return (
    <form className="rounded-xl border border-[#c9a84c]/20 bg-[#c9a84c]/[0.03] p-4 grid grid-cols-1 md:grid-cols-4 gap-3"
      onSubmit={(e) => { e.preventDefault(); if (ok) onSave(f); }} data-testid="cdm-expense-form">
      <label className="text-xs text-gray-400 space-y-1"><span>Fecha</span><input type="date" value={f.date} onChange={set('date')} className={`${inputCls} w-full`} required /></label>
      <label className="text-xs text-gray-400 space-y-1"><span>Partida</span>
        <select value={f.category} onChange={set('category')} className={`${inputCls} w-full`} required data-testid="cdm-expense-category">
          <option className="bg-[#0d1829]" value="">Elige…</option>
          {categories.map((c) => <option className="bg-[#0d1829]" key={c} value={c}>{c}</option>)}
        </select>
      </label>
      <label className="text-xs text-gray-400 space-y-1 md:col-span-2"><span>Concepto</span><input value={f.concept} onChange={set('concept')} className={`${inputCls} w-full`} maxLength={300} placeholder="Ej.: campaña Meta octubre" data-testid="cdm-expense-concept" /></label>
      <label className="text-xs text-gray-400 space-y-1"><span>Proveedor</span><input value={f.supplier} onChange={set('supplier')} className={`${inputCls} w-full`} maxLength={200} /></label>
      <label className="text-xs text-gray-400 space-y-1"><span>Importe</span><input type="number" min="0.01" step="0.01" value={f.amount} onChange={set('amount')} className={`${inputCls} w-full`} required data-testid="cdm-expense-amount" /></label>
      <label className="text-xs text-gray-400 space-y-1"><span>Divisa</span>
        <select value={f.currency} onChange={set('currency')} className={`${inputCls} w-full`}><option className="bg-[#0d1829]">EUR</option><option className="bg-[#0d1829]">USD</option></select>
      </label>
      <label className="text-xs text-gray-400 flex items-center gap-2 pt-5"><input type="checkbox" checked={!!f.recurring} onChange={set('recurring')} /> Gasto recurrente</label>
      <label className="text-xs text-gray-400 space-y-1 md:col-span-4"><span>Notas</span><input value={f.notes || ''} onChange={set('notes')} className={`${inputCls} w-full`} maxLength={2000} /></label>
      <div className="md:col-span-4 flex gap-2 justify-end">
        <button type="button" onClick={onCancel} className={btnGhost}><X size={14} /> Cancelar</button>
        <button type="submit" disabled={!ok || busy} className={btnGold} data-testid="cdm-expense-save">Guardar gasto</button>
      </div>
    </form>
  );
}

export default function CdmGastos() {
  const [dialog, ask] = useConfirm();
  const { data, error, reload } = useLoad(() => dashboardApi.cdmExpenses(), []);
  const [month, setMonth] = useState('');
  const [cat, setCat] = useState('');
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState(null); // null | 'new' | expense
  const [busy, setBusy] = useState(false);

  const all = useMemo(() => data?.expenses || [], [data]);
  const months = useMemo(() => [...new Set(all.map((g) => (g.date || '').slice(0, 7)).filter(Boolean))].sort().reverse(), [all]);
  const rows = useMemo(() => all.filter((g) => (!month || (g.date || '').startsWith(month))
    && (!cat || g.category === cat)
    && (!q || [g.id, g.concept, g.supplier, g.category].some((v) => (v || '').toLowerCase().includes(q.toLowerCase())))), [all, month, cat, q]);
  const byCat = useMemo(() => {
    const m = {};
    rows.forEach((g) => { m[g.category || 'Sin partida'] = (m[g.category || 'Sin partida'] || 0) + (g.amount_eur || 0); });
    return Object.entries(m).sort((a, b) => b[1] - a[1]);
  }, [rows]);
  if (!data) return <Loading error={error} />;

  const categories = data.categories || [];
  const total = rows.reduce((a, g) => a + (g.amount_eur || 0), 0);
  const recurring = rows.filter((g) => g.recurring).reduce((a, g) => a + (g.amount_eur || 0), 0);

  const save = async (f) => {
    setBusy(true);
    try {
      const payload = { date: f.date, category: f.category, concept: f.concept || null, supplier: f.supplier || null,
        amount: Number(f.amount), currency: f.currency, recurring: !!f.recurring, notes: f.notes || null };
      if (editing === 'new') {
        await dashboardApi.cdmOperation({ kind: 'expense', ...payload });
        toast.success('Gasto registrado');
      } else {
        await dashboardApi.cdmPatchExpense(editing.id, payload);
        toast.success('Gasto actualizado');
      }
      setEditing(null);
      reload();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const remove = async (g) => {
    const ok = await ask({
      title: `Borrar el gasto ${g.id}`,
      details: [
        { label: 'Concepto', value: g.concept || g.category || '—' },
        { label: 'Importe', value: money(g.amount, g.currency) },
        { label: 'Fecha', value: fdate(g.date) },
        'Desaparece de los cuadros de gastos y resultado. El registro completo queda en el historial de cambios.',
      ],
      confirmLabel: 'Borrar gasto',
    });
    if (!ok) return;
    try { await dashboardApi.cdmDeleteExpense(g.id); toast.success('Gasto borrado'); reload(); } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="space-y-6" data-testid="cdm-gastos">
      {dialog}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label={month ? `Gastos ${month}` : 'Gastos (todo)'} value={eur(total)} sub={`${rows.length} apuntes`} />
        <Kpi label="Recurrentes" value={eur(recurring)} />
        <Kpi label="Partida principal" value={byCat[0] ? byCat[0][0] : '—'} sub={byCat[0] ? eur(byCat[0][1]) : ''} />
        <Kpi label="Costes fijos comprometidos" value={eur((data.committed || []).reduce((a, c) => a + (Number(c.eur ?? c.amount_eur) || 0), 0))} sub="al mes (Ajustes)" />
      </div>

      {editing ? (
        <ExpenseForm key={editing === 'new' ? 'new' : editing.id} categories={categories} busy={busy} onCancel={() => setEditing(null)} onSave={save}
          initial={editing === 'new' ? emptyExpense() : { ...emptyExpense(), ...editing, amount: editing.amount, notes: editing.notes || '', concept: editing.concept || '', supplier: editing.supplier || '' }} />
      ) : (
        <button type="button" className={btnGold} onClick={() => setEditing('new')} data-testid="cdm-expense-add"><Plus size={14} /> Añadir gasto</button>
      )}

      <Block title={`Gastos (${rows.length})`} right={<Search value={q} onChange={setQ} placeholder="Buscar concepto o proveedor" />}>
        <div className="flex flex-wrap gap-2">
          <select value={month} onChange={(e) => setMonth(e.target.value)} className={inputCls} aria-label="Mes" data-testid="cdm-expense-month">
            <option className="bg-[#0d1829]" value="">Todos los meses</option>
            {months.map((m) => <option className="bg-[#0d1829]" key={m} value={m}>{m}</option>)}
          </select>
          <select value={cat} onChange={(e) => setCat(e.target.value)} className={inputCls} aria-label="Partida">
            <option className="bg-[#0d1829]" value="">Todas las partidas</option>
            {categories.map((c) => <option className="bg-[#0d1829]" key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <Table rows={rows} empty="No hay gastos con estos filtros" cols={[
          { key: 'date', label: 'Fecha', render: (g) => fdate(g.date) },
          { key: 'id', label: 'ID', className: 'text-white font-medium' },
          { key: 'category', label: 'Partida', render: (g) => <Badge>{g.category || '—'}</Badge> },
          { key: 'concept', label: 'Concepto', render: (g) => (<div>{g.concept || '—'}{g.supplier && <p className="text-xs text-gray-400">{g.supplier}</p>}</div>) },
          { key: 'amount', label: 'Importe', align: 'right', render: (g) => money(g.amount, g.currency) },
          { key: 'amount_eur', label: 'EUR', align: 'right', render: (g) => eur(g.amount_eur) },
          { key: 'recurring', label: 'Recurrente', render: (g) => (g.recurring ? 'Sí' : '') },
          { key: 'actions', label: '', noSort: true, render: (g) => (
            <div className="flex gap-1 justify-end">
              <button type="button" onClick={() => setEditing(g)} className="p-1.5 rounded hover:bg-white/[0.06]" aria-label={`Editar ${g.id}`}><Pencil size={13} /></button>
              <button type="button" onClick={() => remove(g)} className="p-1.5 rounded hover:bg-red-500/10 text-red-300" aria-label={`Borrar ${g.id}`}><Trash2 size={13} /></button>
            </div>) },
        ]} />
      </Block>

      {byCat.length > 0 && (
        <Block title="Por partida">
          <Table rows={byCat.map(([k, v]) => ({ id: k, category: k, eur: v, share: total ? v / total : 0 }))} cols={[
            { key: 'category', label: 'Partida', className: 'text-white' },
            { key: 'eur', label: 'EUR', align: 'right', render: (r) => eur(r.eur) },
            { key: 'share', label: '% del total', align: 'right', render: (r) => `${Math.round(r.share * 100)} %` },
          ]} />
        </Block>
      )}
    </div>
  );
}
